import { animate, useReducedMotion } from "motion/react";
import { useLayoutEffect, useRef } from "react";

import { cn } from "@/lib/utils/cn";

export const BALANCE_EASE = [0.16, 1, 0.3, 1] as const;

type AnimatedNumberProps = {
  value: number;
  format: (value: number) => string;
  title?: string;
  className?: string;
};

export function AnimatedNumber({ value, format, title, className }: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef<number | null>(null);
  const formatRef = useRef(format);
  const reduceMotion = useReducedMotion();

  useLayoutEffect(() => {
    formatRef.current = format;
    // A new formatter (e.g. after a language switch) must redraw the number that's already shown.
    if (ref.current && shown.current !== null) ref.current.textContent = format(Math.round(shown.current * 100) / 100);
  });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const from = shown.current;
    const write = (current: number) => {
      shown.current = current;
      node.textContent = formatRef.current(Math.round(current * 100) / 100);
    };

    if (from === null || from === value || reduceMotion) {
      write(value);
      return;
    }

    const controls = animate(from, value, { duration: 0.6, ease: BALANCE_EASE, onUpdate: write });
    return () => controls.stop();
  }, [value, reduceMotion]);

  return <span ref={ref} title={title} className={cn("tabular-nums", className)} />;
}
